/**
 * Collapsible "New loan" form. Self-gates on `loan.write`.
 *
 * A loan ties together four existing aggregates, so this form leads with
 * pickers (customer / dealer / device / contract) populated from their list
 * endpoints — the queries are lazy (`enabled: open`) so opening the page
 * doesn't fetch four extra lists.
 *
 * The repayment schedule is no longer typed by hand: it is *generated* from the
 * selected device's model pricing (cost / lending price / down payment) plus
 * the chosen repayment frequency and tenor, via `@dfp/domain`'s
 * `generateSchedule`. The financed lending price = per-period x tenor (or the
 * fixed total); the down payment is deducted, so `deposit` = down payment and
 * `principal` = lending price − down payment (= the sum of the installments).
 * Each installment splits principal (device cost share) and interest (margin).
 *
 * Datetimes come from native `datetime-local` inputs and are converted to
 * ISO-8601 at submit. All money is in minor units to match the API 1:1.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generateSchedule, type LendingPrice, type RepaymentFrequency } from "@dfp/domain";
import type {
  Contract,
  CreateLoanRequest,
  Customer,
  Dealer,
  Device,
  Loan,
  ScheduleEntry,
} from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";

// Repayment frequencies + tenor bounds come from country policy (the
// GET /api/country-config `loanPolicy`); these fallbacks apply only while it
// is still loading.
const FALLBACK_FREQUENCIES: readonly RepaymentFrequency[] = ["monthly"];
const FALLBACK_MAX_TENOR = 9;

interface LendingInput {
  readonly costMinor: number;
  readonly lendingPrice: LendingPrice;
  readonly downPaymentMinor: number;
  readonly currency: string;
}

/** Map a device's pricing fields to schedule-generator input, or null if unpriced. */
function lendingInputFor(device: Device): LendingInput | null {
  if (
    device.lendingPriceMode === null ||
    device.lendingPriceMinor === null ||
    device.downPaymentMinor === null
  ) {
    return null;
  }
  return {
    costMinor: device.valueAmountMinor,
    lendingPrice:
      device.lendingPriceMode === "per_period"
        ? {
            mode: "per_period",
            perPeriod: { basis: "amount", perPeriodMinor: device.lendingPriceMinor },
          }
        : { mode: "fixed", totalMinor: device.lendingPriceMinor },
    downPaymentMinor: device.downPaymentMinor,
    currency: device.valueCurrency,
  };
}

/** datetime-local string → ISO-8601, or null if absent/invalid. */
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function money(minor: number, currency: string): string {
  return `${minor.toLocaleString()} ${currency}`;
}

export function CreateLoanForm(): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("loan.write");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
    enabled: open,
  });
  const dealers = useQuery({
    queryKey: ["dealers", "list"],
    queryFn: () => api.get<readonly Dealer[]>("/api/dealers"),
    enabled: open,
  });
  const devices = useQuery({
    queryKey: ["devices", "list"],
    queryFn: () => api.get<readonly Device[]>("/api/devices"),
    enabled: open,
  });
  const contracts = useQuery({
    queryKey: ["contracts", "list"],
    queryFn: () => api.get<readonly Contract[]>("/api/contracts"),
    enabled: open,
  });
  const cfg = useCountryConfig(open);

  const [customerId, setCustomerId] = useState("");
  const [dealerId, setDealerId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [contractId, setContractId] = useState("");
  const [frequency, setFrequency] = useState<RepaymentFrequency>("monthly");
  const [tenorMonths, setTenorMonths] = useState("3");
  const [startAt, setStartAt] = useState("");
  const [policyVersionId, setPolicyVersionId] = useState("");
  const [nextEvalAt, setNextEvalAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateLoanRequest) => api.post<Loan>("/api/loans", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["loans", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  const selectedDevice = (devices.data ?? []).find((d) => d.id === deviceId) ?? null;

  const policy = cfg.data?.loanPolicy ?? null;
  const frequencies = policy?.repaymentFrequencies ?? FALLBACK_FREQUENCIES;
  const minTenor = policy?.minTenorMonths ?? 1;
  const maxTenor = policy?.maxTenorMonths ?? FALLBACK_MAX_TENOR;
  const tenors = Array.from(
    { length: Math.max(0, maxTenor - minTenor + 1) },
    (_, i) => minTenor + i,
  );

  // Generate the schedule whenever the device pricing + terms are all present.
  const { lending, generated, genError } = useMemo(() => {
    const li = selectedDevice ? lendingInputFor(selectedDevice) : null;
    if (!li) return { lending: null, generated: null, genError: null as string | null };
    const tenor = Number(tenorMonths);
    const start = startAt ? new Date(startAt) : null;
    if (
      !Number.isInteger(tenor) ||
      tenor < minTenor ||
      tenor > maxTenor ||
      !start ||
      Number.isNaN(start.getTime())
    ) {
      return { lending: li, generated: null, genError: null };
    }
    try {
      const g = generateSchedule({
        costMinor: li.costMinor,
        lendingPrice: li.lendingPrice,
        downPaymentMinor: li.downPaymentMinor,
        frequency,
        tenorMonths: tenor,
        startAt: start,
      });
      return { lending: li, generated: g, genError: null };
    } catch (e) {
      return {
        lending: li,
        generated: null,
        genError: e instanceof Error ? e.message : "Cannot generate schedule.",
      };
    }
  }, [selectedDevice, frequency, tenorMonths, startAt, minTenor, maxTenor]);

  function reset(): void {
    setCustomerId("");
    setDealerId("");
    setDeviceId("");
    setContractId("");
    setFrequency("monthly");
    setTenorMonths("3");
    setStartAt("");
    setPolicyVersionId("");
    setNextEvalAt("");
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (!customerId || !dealerId || !deviceId || !contractId) {
      setError("Customer, dealer, device and contract are all required.");
      return;
    }
    if (!lending) {
      setError("The selected device has no pricing. Set its lending price + down payment first.");
      return;
    }
    if (genError) {
      setError(genError);
      return;
    }
    if (!generated) {
      setError("Choose a repayment frequency, tenor and start date to generate the schedule.");
      return;
    }
    if (policyVersionId.trim().length < 1) {
      setError("Policy version id is required.");
      return;
    }
    const nextEval = toIso(nextEvalAt);
    if (!nextEval) {
      setError("Next evaluation date is required.");
      return;
    }

    const currency = lending.currency;
    // ADR-0014: installments repay the FULL financed total (deposit is collected
    // separately, on top), so the origination principal = the financed total.
    const principalMinor = generated.lendingPriceMinor;
    const schedule: ScheduleEntry[] = generated.installments.map((i) => ({
      installmentNumber: i.installmentNumber,
      dueAt: i.dueAt.toISOString(),
      principalMinor: i.principalMinor,
      interestMinor: i.interestMinor,
      feesMinor: i.feesMinor,
    }));

    mutation.mutate({
      customerId,
      dealerId,
      deviceId,
      contractId,
      principal: { amountMinor: principalMinor, currency },
      deposit: { amountMinor: generated.downPaymentMinor, currency },
      schedule,
      nextEvalAt: nextEval,
      policyVersionId: policyVersionId.trim(),
      repaymentFrequency: frequency,
      tenorMonths: Number(tenorMonths),
    });
  }

  if (!canWrite) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New loan…</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New loan</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Customer">
          <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select customer…</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.givenName} {c.familyName} · {c.phoneMasked}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Dealer">
          <Select value={dealerId} onChange={(e) => setDealerId(e.target.value)}>
            <option value="">Select dealer…</option>
            {(dealers.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} · {d.riskTier}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Device">
          <Select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
            <option value="">Select device…</option>
            {(devices.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.brand} {d.model} · {d.imei}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Contract">
          <Select value={contractId} onChange={(e) => setContractId(e.target.value)}>
            <option value="">Select contract…</option>
            {(contracts.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.signedByCustomer} · v{c.version}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Policy version id">
          <TextInput value={policyVersionId} onChange={(e) => setPolicyVersionId(e.target.value)} />
        </Field>
        <Field label="Next evaluation">
          <TextInput
            type="datetime-local"
            value={nextEvalAt}
            onChange={(e) => setNextEvalAt(e.target.value)}
          />
        </Field>
        <Field label="Repayment frequency">
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RepaymentFrequency)}
          >
            {frequencies.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Tenor" hint="months">
          <Select value={tenorMonths} onChange={(e) => setTenorMonths(e.target.value)}>
            {tenors.map((t) => (
              <option key={t} value={String(t)}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Start date">
          <TextInput
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </Field>
      </div>

      {/* Device pricing + generated schedule preview */}
      {selectedDevice && lending === null ? (
        <p className="text-xs text-amber-700">
          This device has no model pricing yet. Set its lending price and down payment on the device
          page before financing it.
        </p>
      ) : null}

      {lending ? (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2 text-xs text-slate-700">
          <div className="grid grid-cols-3 gap-2">
            <span>Cost: {money(lending.costMinor, lending.currency)}</span>
            <span>
              Lending price:{" "}
              {lending.lendingPrice.mode === "per_period"
                ? lending.lendingPrice.perPeriod.basis === "amount"
                  ? `${money(lending.lendingPrice.perPeriod.perPeriodMinor, lending.currency)} / month + cost`
                  : `${(lending.lendingPrice.perPeriod.rateBps / 100).toFixed(2)}% of cost / month`
                : lending.lendingPrice.mode === "fixed"
                  ? `${money(lending.lendingPrice.totalMinor, lending.currency)} fixed`
                  : "per-tenor ladder"}
            </span>
            <span>Down payment: {money(lending.downPaymentMinor, lending.currency)}</span>
          </div>

          {genError ? <p className="text-amber-700">{genError}</p> : null}

          {generated ? (
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-2 font-medium text-slate-800">
                <span>Deposit: {money(generated.downPaymentMinor, lending.currency)}</span>
                <span>
                  Principal (financed):{" "}
                  {money(
                    generated.lendingPriceMinor - generated.downPaymentMinor,
                    lending.currency,
                  )}
                </span>
                <span>{generated.installments.length} installment(s)</span>
              </div>
              <table className="w-full text-left">
                <thead className="text-slate-500">
                  <tr>
                    <th className="font-normal">#</th>
                    <th className="font-normal">Due</th>
                    <th className="font-normal">Principal</th>
                    <th className="font-normal">Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {generated.installments.slice(0, 6).map((i) => (
                    <tr key={i.installmentNumber}>
                      <td>{i.installmentNumber}</td>
                      <td>{i.dueAt.toISOString().slice(0, 10)}</td>
                      <td>{i.principalMinor.toLocaleString()}</td>
                      <td>{i.interestMinor.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {generated.installments.length > 6 ? (
                <p className="text-slate-500">…and {generated.installments.length - 6} more</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create loan"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
